import { coalesceGet, invalidateCoalesced } from "./coalesceGet";
import { getHeadersFiles } from "./middleware";
import { handleServiceError } from "./errorHandler";
import { REACT_APP_BACKEND_API } from "../config";
import { createLogger } from '../utils/logger';
import { sentryFetch } from "./sentryApiWrapper";

const serviceLogger = createLogger('QUOTE_FORM_SUMMARY_SERVICE');

const BASE_URL = REACT_APP_BACKEND_API + 'asa-quote';
const SUMMARY_BASE_URL = REACT_APP_BACKEND_API + 'summary';

// Interfaces para campos financieros
export interface FinancialAsset {
    residential_property: number;
    other_property: number;
    personal_effects: number;
    investments: number;
    cash: number;
    others: number;
    total_assets: number;
}

export interface FinancialLiability {
    mortgages: number;
    loan: number;
    others: number;
    total_liabilities: number;
}

export interface BalanceSheet {
    assets_liabilities_balance: number;
}

export interface Financials {
    assets: FinancialAsset[];
    liabilities: FinancialLiability[];
    balance_sheet: BalanceSheet;
}

// Interface para seguro de salud
export interface HealthInsurance {
    company: string;
    coverage_amount: number | null;
    effective_date: string | null;
}

// Interface para ocupación
export interface Occupation {
    description: string;
    name: string;
    main?: boolean;
    other_name?: string;
}

// Interface para riders
export interface Riders {
    add: number;
    ci: string;
    cp: string;
    ptd: string;
    rop: number;
    ttd: string;
    with_extra_charge: string;
}

export interface UpdateQuestionValuesQuoteFormSummaryRequest {
    Email?: string;
    Phone?: string;
    Document_type?: string;
    Document_number?: string;
    Gender?: string;
    Smoker?: string;
    Country?: string;
    BirthDate?: string;
    AnnualIncomeLocal?: number;
    MonthlyIncomeLocal?: number;
    LocalCurrency?: string;
    Plan?: string;
    Term?: number;
    Premium?: number;
    Frequency?: string;
    MainCoverageAmount?: number;
    Age?: number;
    OwnerEqualsInsured?: string;
    Beneficiaries?: any[];
    HasRop?: boolean;
    HasIncomeReplacement?: boolean;
    HasCriticalIllness?: boolean;
    HasCancerProtection?: boolean;
    RelationOwnerInsured?: string;
    OccupationMain?: string;
    PepNone?: boolean;
    ExtremeSportNone?: boolean;
    Bmi?: number;
    HadWeightLoss?: boolean;
    ReasonWeightLoss?: number;
    HealthProblemsNone?: boolean;
    FamilyDiedBefore65?: boolean;
    AnyPolicyRestriction?: boolean;
    UsesDrugs?: boolean;
    OnlyUsesMarijuanaSporadically?: boolean;
    ConsumesAlcohol?: string;
    PurposePolicy?: number[];
    HasHealthAddOn?: boolean;
    LatestBpReadingValue?: string;
    LatestBpReadingTimeRange?: string;
    LatestCholesterolReadingValue?: string;
    LatestCholesterolTimeRange?: string;
    LatestTriglyceridesReadingValue?: string;
    LatestTriglyceridesTimeRange?: string;
    FamilyDied?: boolean;
    QuoteID?: string;
    ProposalPolicy?: string;
    Language?: string;
    State?: string;
    ZipCode?: string;
    FirstName?: string;
    LastName?: string;
    Weight?: string;
    Height?: string;
    Region?: string;
    UrlSummary?: string;
    UrlDocument?: string;
    Step?: string;
    PaymentSession?: string;
    City?: string;
    Address?: string;
    HouseNumber?: string;
    InternalHouseNumber?: string;
    Neighborhood?: string;
    AddressReference?: string;
    PayerFirstName?: string;
    PayerLastName?: string;
    NationalIdentity?: string;
    TaxIdentity?: string;
    HolderDocumentType?: string;
    HolderDocumentNumber?: string;
    HolderFirstName?: string;
    HolderLastName?: string;
    HolderBirthDate?: string;
    HolderAge?: number;
    HolderGender?: string;
    HolderAddress?: string;
    HolderState?: string;
    HolderCity?: string;
    HolderNeighborhood?: string;
    HolderHouseNumber?: string;
    HolderInternalHouseNumber?: string;
    HolderNationalIdentity?: string;
    HolderTaxIdentity?: string;
    HolderPhone?: string;
    HolderEmail?: string;
    TaxStatus?: string;
    BirthCountry?: string;
    Municipality?: string;
    HolderTaxStatus?: string;
    HolderBirthCountry?: string;
    HolderMunicipality?: string;
    MiddleName?: string;
    SecondLastName?: string;
    HolderMiddleName?: string;
    HolderSecondLastName?: string;
    AA?: boolean;
    diseases_selected?: string[];
    diseases_selected_page_1?: string[];
    diseases_selected_page_2?: string[];
    diseases_selected_page_3?: string[];
    excluded_occupation?: boolean;
    extra_premium?: string;
    financials?: Financials;
    have_health_insurance?: boolean;
    health_insurance?: HealthInsurance[];
    mib?: string;
    occupation?: Occupation[];
    other_applications?: boolean;
    reinsurer?: string;
    riders?: Riders;
    fiscal_residence?: boolean;
    fiscal_residence_address?: string;
    fiscal_residence_zip_code?: string;
    fiscal_residence_country?: string;
    fiscal_residence_state?: string;
    fiscal_residence_city?: string;
    is_us_citizen?: boolean;
    tax_id_tin?: string;
    files?: any[];
    credit_banking_entity?: string;
    credit_amount?: number;
    credit_files?: string;
    kp_applicant_position?: string;
    kp_company_name?: string;
    kp_business_nature?: string;
    kp_employee_count?: string;
    kp_company_founded_date?: string;
    kp_is_capital_owner?: boolean;
    kp_capital_proportion?: string;
    kp_org_chart_file?: string;
    kp_constitution_file?: string;
    kp_financial_statements_file?: string;
    kp_policy_reason?: string;
    kp_policy_duration?: string;
    kp_premium_resource_origin?: string;
    kp_insured_name?: string;
    kp_sum_calculation_mechanism?: string;
    kp_gross_net_proportion?: string;
    kp_value_reason?: string;
    kp_is_in_bankruptcy?: boolean;
    kp_tax_deduction_intent?: boolean;
    kp_has_service_contract?: boolean;
    kp_contract_start_date?: string;
    kp_contract_policy_duration?: string;
    kp_has_successor?: boolean;
    kp_successor_full_name?: string;
    kp_successor_birth_date?: string;
    kp_successor_has_contract?: boolean;
    kp_successor_contract_date?: string;
    social_name?: string;
}

export interface UpdateQuoteFormSummaryQuoteResponse {
    message: string;
}

export interface QuoteFormSummaryQuoteSummary {
    id: number;
    first_name: string;
    middle_name: string;
    last_name: string;
    second_last_name: string;
    email: string;
    phone: string;
    document_type: string;
    document_number: string;
    gender: string;
    smoker: string;
    country: string;
    birth_date: string;
    annual_income_local: number;
    monthly_income_local: number;
    local_currency: string;
    plan: string;
    term: number;
    premium: number;
    frequency: string;
    main_coverage_amount: number;
    age: number;
    owner_equals_insured: string;
    beneficiaries: any[];
    has_rop: boolean;
    has_income_replacement: boolean;
    has_critical_illness: boolean;
    has_cancer_protection: boolean;
    relation_owner_insured: string;
    occupation_main: string;
    pep_none: boolean;
    extreme_sport_none: boolean;
    bmi: number;
    had_weight_loss: boolean;
    reason_weight_loss: number;
    health_problems_none: boolean;
    family_died_before_65: boolean;
    any_policy_restriction: boolean;
    uses_drugs: boolean;
    only_uses_marijuana_sporadically: boolean;
    consumes_alcohol: string;
    purpose_policy: number[];
    has_health_add_on: boolean;
    latest_bp_reading_value: string;
    latest_bp_reading_time_range: string;
    latest_cholesterol_reading_value: string;
    latest_cholesterol_reading_time_range: string;
    latest_triglycerides_reading_value: string;
    latest_triglycerides_reading_time_range: string;
    family_died: boolean;
    step: string;
    payment_session: string;
    national_identity: string;
    tax_identity: string;
    holder_first_name: string;
    holder_last_name: string;
    holder_birth_date: string;
    holder_age: number;
    holder_gender: string;
    holder_address: string;
    holder_national_identity: string;
    holder_tax_identity: string;
    holder_phone: string;
    holder_email: string;
}

export interface QuoteFormSummaryQuoteSummaryResponse {
    data: QuoteFormSummaryQuoteSummary | null;
    found: boolean;
}

export interface QuoteFormSummaryQuoteErrorResponse {
    message: string;
    status: number;
    error: string;
}

/**
 * Realiza un upsert de una cotización Quote Form Summary en el servidor
 * @param id ID de la cotización Quote Form Summary
 * @param request Datos de la cotización
 * @returns Promesa con la respuesta del upsert
 */
export const upsertQuoteFormSummaryQuote = async (id: string, request: UpdateQuestionValuesQuoteFormSummaryRequest): Promise<UpdateQuoteFormSummaryQuoteResponse> => {
    invalidateCoalesced(`qfs-summary:${id}`);
    try {
        const url = `${BASE_URL}/${id}`;

        const response = await sentryFetch(
            url,
            {
                method: 'PUT',
                headers: getHeadersFiles(),
                body: JSON.stringify(request)
            },
            {
                operation: 'upsert_quote_form_summary',
                quoteId: id,
                email: request.Email,
                documentNumber: request.Document_number,
                plan: request.Plan,
                step: request.Step
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            serviceLogger.error('Error al realizar upsert de cotización Quote Form Summary:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData
            });
            throw new Error(`Error: ${response.status} - ${errorData.message || 'Error al realizar upsert de cotización Quote Form Summary'}`);
        }

        const responseData = await response.json() as UpdateQuoteFormSummaryQuoteResponse;
        return responseData;
    } catch (error) {
        serviceLogger.error('Error general en upsertQuoteFormSummaryQuote:', error);
        handleServiceError(error);
        throw error;
    } finally {
        invalidateCoalesced(`qfs-summary:${id}`);
    }
};

/**
 * Obtiene información completa de una cotización Quote Form Summary por ID de cotización
 * @param quoteId ID de la cotización Quote Form Summary
 * @returns Promesa con la información completa de la cotización o null si no se encuentra
 */
export const getQuoteFormSummaryQuoteSummary = async (quoteId: string): Promise<QuoteFormSummaryQuoteSummary | null> => {
  // In-flight only. A stored body would hide a status or signature change.
  return coalesceGet(`qfs-summary:${quoteId}`, async () => {
    try {
        const url = `${SUMMARY_BASE_URL}/${quoteId}`;

        const response = await sentryFetch(
            url,
            {
                method: 'GET',
                headers: getHeadersFiles()
            },
            {
                operation: 'get_quote_form_summary',
                quoteId
            }
        );

        if (!response.ok) {
            const errorData = await response.json() as QuoteFormSummaryQuoteErrorResponse;
            serviceLogger.error('Error al obtener resumen de cotización Quote Form Summary:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData
            });
            throw new Error(`Error: ${response.status} - ${errorData.message || 'Error al obtener resumen de cotización Quote Form Summary'}`);
        }

        const responseData = await response.json() as QuoteFormSummaryQuoteSummaryResponse;

        if (!responseData.found) {
            serviceLogger.info('Cotización Quote Form Summary no encontrada para ID:', quoteId);
            return null;
        }

        return responseData.data;
    } catch (error) {
        serviceLogger.error('Error general en getQuoteFormSummaryQuoteSummary:', error);
        handleServiceError(error);
        throw error;
    }
  }, { ttlMs: 0 });
}; 